/**
 * Locale display helpers — mirrors backend {@link normalizeLocaleCode} in bidgely-quill.
 */

import type { StringRegistryItem } from '../types/stringRegistryTypes';
import { getGlobalLocaleOverrides } from './globalTemplateLocales';

export function normalizeLocaleCode(locale: string | undefined | null): string {
  if (locale == null || typeof locale !== "string") return "";
  const trimmed = locale.trim();
  if (!trimmed) return "";
  let code = trimmed.replace(/_/g, "-");
  if (code === "en-US") return "en";
  return code;
}

export function safeLocaleFieldKey(code: string | undefined | null): string {
  const n = normalizeLocaleCode(code);
  if (!n) return "_";
  return n.replace(/[^a-zA-Z0-9]/g, "_");
}

export function getLocaleValue(
  localeValues: { locale?: string; value?: string }[] | undefined,
  locale: string | undefined | null
): string {
  const t = normalizeLocaleCode(locale);
  if (!t) return "";
  const row = localeValues?.find((l) => l?.locale && normalizeLocaleCode(l.locale) === t);
  return row?.value ?? "";
}

/** Effective template string: template override wins, else global entry value for that locale. */
export function resolvedTemplateLocaleDisplay(
  localeValues: { locale?: string; value?: string }[] | undefined,
  overrides: { locale?: string; value?: string }[] | undefined,
  locale: string | undefined | null
): { text: string; fromOverride: boolean } {
  const t = normalizeLocaleCode(locale);
  if (!t) {
    return { text: "—", fromOverride: false };
  }
  const o = overrides?.find((x) => x?.locale && normalizeLocaleCode(x.locale) === t);
  if (o) {
    const v = o.value != null ? String(o.value) : "";
    return { text: v || "—", fromOverride: true };
  }
  const g = getLocaleValue(localeValues, locale);
  return { text: g || "—", fromOverride: false };
}

export function truncateText(s: string | undefined, max = 56): string {
  if (s == null || s === "" || s === "—") return s ?? "—";
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

type LocaleCatalogEntry = { code: string };

type ItemForSummary = {
  stringRegistryEntry?: StringRegistryItem;
  localeOverrides?: { locale: string; value?: string }[];
};

function globalLocalesForItem(item: ItemForSummary): { locale: string; value?: string }[] {
  return getGlobalLocaleOverrides(item.stringRegistryEntry);
}

/** One-line preview for grid (many locales). */
export function buildTemplateItemLocaleSummaryShort(
  item: ItemForSummary,
  catalog: LocaleCatalogEntry[],
  maxTotal = 140
): string {
  const parts = catalog
    .filter((c) => normalizeLocaleCode(c.code))
    .map(({ code }) => {
      const norm = normalizeLocaleCode(code);
      const { text } = resolvedTemplateLocaleDisplay(
        globalLocalesForItem(item),
        item.localeOverrides,
        code
      );
      return `${norm}: ${truncateText(text, 14)}`;
    });
  return truncateText(parts.join(" · "), maxTotal);
}

/** Multiline tooltip: effective value + source (global vs template). */
export function buildTemplateItemLocaleTooltip(item: ItemForSummary, catalog: LocaleCatalogEntry[]): string {
  return catalog
    .filter((c) => normalizeLocaleCode(c.code))
    .map(({ code }) => {
      const norm = normalizeLocaleCode(code);
      const { text, fromOverride } = resolvedTemplateLocaleDisplay(
        globalLocalesForItem(item),
        item.localeOverrides,
        code
      );
      return `${norm} — ${fromOverride ? "template" : "global"}: ${text}`;
    })
    .join("\n");
}
